import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { Empty, Row, Spin, Typography } from "antd";
import { AxiosError } from "axios";

import './styles.css';

import { IMessageUser } from "../../../interfaces";
import AppUserRow from "../AppUserRow";
import MessagesService from "../../../../../services/http/services/MessagesService";
import { EmitError } from "../../../../../utils/EmitError";
import { onlyNumbers } from "../../../../../utils/Masks";

const { Text } = Typography;

interface IProps {
    filters: {
        name: string;
        cpf: string;
    };
}

export default function AppUsersList({ filters }: IProps) {
    const [appUsers, setAppUsers] = useState<Array<IMessageUser>>([]);
    const [filteredAppUsers, setFilteredAppUsers] = useState<Array<IMessageUser>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingFiltered, setIsLoadingFiltered] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState(true);
    const [pageFiltered, setPageFiltered] = useState<number>(1);
    const [hasMoreFiltered, setHasMoreFiltered] = useState(true);
    const { id } = useParams();

    const isSearching = useMemo(() => {
        return filters.name.length >= 3 || filters.cpf.length >= 3;
    }, [filters]);

    const loadMore = useCallback(async () => {
        if (isLoading) {
            return;
        }

        const controller = new AbortController();

        try {
            setIsLoading(true);

            const appUsersList = await MessagesService.listMessageAppUsers(Number(id), page, controller.signal);

            if (appUsersList.length === 0) {
                setHasMore(false);
            }

            setAppUsers((prevState) => [...prevState, ...appUsersList]);
            setPage((prevState) => prevState + 1);
            setIsLoading(false);
        } catch (err) {
            if (err instanceof AxiosError && err.name === "CanceledError") {
                return;
            }

            setIsLoading(false);
            EmitError(err);
        }

        return () => {
            controller.abort();
        };
    }, [id, page, isLoading]);

    const loadMoreFiltered = useCallback(async () => {
        if (isLoadingFiltered) {
            return;
        }

        const controller = new AbortController();

        try {
            setIsLoadingFiltered(true);

            const appUsersList = await MessagesService.listMessageAppUsers(
                Number(id),
                pageFiltered,
                controller.signal,
                {
                    ...filters,
                    cpf: onlyNumbers(filters.cpf),
                }
            );

            if (appUsersList.length === 0) {
                setHasMoreFiltered(false);
            }

            setFilteredAppUsers((prevState) => [...prevState, ...appUsersList]);
            setPageFiltered((prevState) => prevState + 1);
            setIsLoadingFiltered(false);
        } catch (err) {
            if (err instanceof AxiosError && err.name === "CanceledError") {
                return;
            }

            setIsLoadingFiltered(false);
            EmitError(err);
        }

        return () => {
            controller.abort();
        };
    }, [id, pageFiltered, isLoadingFiltered, filters]);

    useEffect(() => {
        if (!id) {
            return;
        }

        const controller = new AbortController();

        async function loadAppUsers() {
            try {
                setIsLoading(true);

                const appUsersList = await MessagesService.listMessageAppUsers(Number(id), 1, controller.signal);

                setAppUsers(appUsersList);
                if (appUsersList.length < 100) {
                    setHasMore(false);
                }
                setPage(2);
                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === "CanceledError") {
                    return;
                }

                setIsLoading(false);
                EmitError(err);
            }
        }

        loadAppUsers();

        return () => {
            controller.abort();
        };
    }, [id]);

    useEffect(() => {
        if (!id || (!filters.name && !filters.cpf)) {
            setFilteredAppUsers([]);
            return;
        }

        if (!isSearching) {
            return;
        }

        const controller = new AbortController();

        async function loadFilteredAppUsers() {
            try {
                setIsLoadingFiltered(true);

                const appUsersList = await MessagesService.listMessageAppUsers(
                    Number(id),
                    1,
                    controller.signal,
                    {
                        ...filters,
                        cpf: onlyNumbers(filters.cpf),
                    }
                );

                setFilteredAppUsers(appUsersList);

                if (appUsersList.length < 100) {
                    setHasMoreFiltered(false);
                } else {
                    setHasMoreFiltered(true);
                }

                setPageFiltered(2);
                setIsLoadingFiltered(false);
            } catch (err) {
                setIsLoadingFiltered(false);

                if (err instanceof AxiosError && err.name === "CanceledError") {
                    return;
                }

                EmitError(err);
            }
        }

        loadFilteredAppUsers();

        return () => {
            controller.abort();
        };
    }, [id, filters, isSearching]);

    const activeList = isSearching ? filteredAppUsers : appUsers;
    const currentLoading = isSearching ? isLoadingFiltered : isLoading;
    const currentHasMore = isSearching ? hasMoreFiltered : hasMore;
    const currentNext = isSearching ? loadMoreFiltered : loadMore;

    if (!currentLoading && activeList.length === 0) {
        return (
            <div style={{ padding: "30px 0" }}>
                <Empty
                    description={
                        isSearching
                            ? "Nenhum usuário encontrado para esta busca."
                            : "Nenhum usuário cadastrado para esta mensagem."
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </div>
        );
    }

    return (
        <InfiniteScroll
            key={isSearching ? "infinity-scroll-filter" : "infinity-scroll-all"}
            height={360}
            dataLength={activeList.length}
            next={currentNext}
            hasMore={currentHasMore}
            loader={
                <Row
                    key="0"
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 8,
                        padding: "12px 0",
                    }}
                >
                    <Spin size="small" />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Carregando mais usuários...
                    </Text>
                </Row>
            }
            endMessage={
                activeList.length > 0 ? (
                    <Row
                        key="end"
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            padding: "10px 0",
                        }}
                    >
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            • Fim da lista ({activeList.length} usuário{activeList.length === 1 ? '' : 's'}) •
                        </Text>
                    </Row>
                ) : undefined
            }
            style={{
                overflowX: "hidden",
                paddingRight: 4,
            }}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
                {activeList.map((appUser) => (
                    <AppUserRow
                        key={appUser.id}
                        appUser={appUser}
                        filters={filters}
                    />
                ))}
            </div>
        </InfiniteScroll>
    );
}